import { ReactNode } from "react";
import { faSliders } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";

import { ProductListFilterSurface } from "client/analytics/types";
import Accordion from "client/common/components/interactive/Accordion";
import TextButton from "client/common/components/buttons/TextButton";

import { FilterChangeHandler, FilterValue, FilterDefinition } from "../types";
import { filtersWithOptions } from "../utils";
import type { FilterPanelState } from "../hooks";
import AppliedFilters from "./AppliedFilters";
import AppliedFilterTags from "./AppliedFilterTags";
import FilterOptionList from "./FilterOptionList";

import styles from "../styles/FilterPanel.module.less";

type FilterPanelProps<TSearch, TFilterOptions> = {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  /** The options each filter offers, keyed by filter, each carrying the count it would yield. */
  filterOptions: TFilterOptions;
  filters: TSearch;
  onChange: FilterChangeHandler<TSearch, TFilterOptions>;
  hasSelectedFilters: boolean;
  /** Owned by the page, because the chip row above the grid offers the same action. */
  onClearAll: () => void;
  /** Off in the modal, which supplies its own title and reset action. */
  showHeader?: boolean;
  /** Only where each selection refreshes the counts; the modal edits a draft against the applied counts. */
  disablesEmptyOptions?: boolean;
};

const groupLabel = (label: ReactNode, count: number) => (
  <span className={styles.groupLabel}>
    {label}
    {!!count && <span className={styles.groupCount}>({count})</span>}
  </span>
);

const FilterPanel = <TSearch, TFilterOptions>({
  definitions,
  filterOptions,
  filters,
  onChange,
  hasSelectedFilters,
  onClearAll,
  showHeader = true,
  disablesEmptyOptions = false,
}: FilterPanelProps<TSearch, TFilterOptions>) => {
  const renderableFilters = filtersWithOptions({ definitions, filterOptions });

  if (!renderableFilters.length) {
    return null;
  }

  const accordionItems = renderableFilters.map(({ definition, options }) => {
    const selected = definition.selectedFrom(filters);
    return {
      key: definition.key,
      label: groupLabel(definition.label, selected.length),
      content: (
        <div>
          {definition.description}
          {!options.length ? (
            definition.emptyState
          ) : (
            // A new option set needs a fresh FilterOptionList: the search query is local state,
            // so reusing the instance leaves the old query filtering the new options to "No
            // matches". Selecting an option moves counts but not values, so the key holds.
            <FilterOptionList
              key={options.map((option) => option.value).join(",")}
              placeholder={`Find a ${definition.singularLabel.toLocaleLowerCase()}`}
              options={options}
              selected={selected}
              onChange={(next) => onChange(definition.toUpdate(next, filters), definition)}
              disablesEmptyOptions={disablesEmptyOptions}
            />
          )}
        </div>
      ),
    };
  });

  return (
    <div className={styles.panel}>
      {showHeader && (
        <div className={styles.header}>
          <h2 className={styles.title}>
            <Icon className={styles.titleIcon} icon={faSliders} />
            Filters
          </h2>
          <TextButton
            type="button"
            className={styles.clearAll}
            onClick={onClearAll}
            disabled={!hasSelectedFilters}
          >
            Clear all
          </TextButton>
        </div>
      )}
      {/* Keyed on the set with options: Accordion.Multi captures initialOpenKeys once at mount, so a
          filter that appears after a search or category change would otherwise render collapsed. */}
      <Accordion.Multi
        key={accordionItems.map((item) => item.key).join(",")}
        items={accordionItems}
        initialOpenKeys={accordionItems.map((item) => item.key)}
        classNames={{
          header: styles.accordionHeader,
          icon: styles.accordionIcon,
          content: styles.accordionContent,
        }}
      />
    </div>
  );
};

/** The panel dressed for `FilterLayout`'s column. */
const FilterRail = <TSearch, TFilterOptions>({
  showFilterPanel,
  filterOptions,
  definitions,
  filters,
  hasSelectedFilters,
  changeFrom,
  clearAllFrom,
}: FilterPanelState<TSearch, TFilterOptions>) => {
  if (!showFilterPanel || !filterOptions) {
    return null;
  }
  return (
    <FilterPanel<TSearch, TFilterOptions>
      definitions={definitions}
      filterOptions={filterOptions}
      filters={filters}
      onChange={changeFrom(ProductListFilterSurface.Rail)}
      hasSelectedFilters={hasSelectedFilters}
      onClearAll={clearAllFrom(ProductListFilterSurface.Rail)}
      disablesEmptyOptions={true}
    />
  );
};

/** The removable chips above the grid, shown only alongside the panel. */
const AppliedFilterRow = <TSearch, TFilterOptions>({
  isBesideCount,
  showFilterPanel,
  filterOptions,
  definitions,
  filters,
  hasSelectedFilters,
  changeFrom,
  clearAllFrom,
}: FilterPanelState<TSearch, TFilterOptions> & { isBesideCount?: boolean }) => {
  if (!showFilterPanel || !filterOptions) {
    return null;
  }
  return (
    <AppliedFilters
      hasSelectedFilters={hasSelectedFilters}
      onClearAll={clearAllFrom(ProductListFilterSurface.AppliedFilters)}
      isBesideCount={isBesideCount}
    >
      <AppliedFilterTags<TSearch, TFilterOptions>
        definitions={definitions}
        options={filterOptions}
        value={filters}
        onChange={changeFrom(ProductListFilterSurface.AppliedFilters)}
      />
    </AppliedFilters>
  );
};

export default Object.assign(FilterPanel, { Rail: FilterRail, AppliedRow: AppliedFilterRow });
